import { FILTERABLE_BUYBOX_KEYS } from '@/components/buybox-new/composables/useBuyboxData';
import { useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { NewBuyBoxView } from '@/stores/user.store';
import { MaybeRefOrGetter, injectLocal, toValue } from '@vueuse/core';
import { ComputedRef, Ref, computed, ref } from 'vue';
import { INTERNATIONAL_OFFERS_ENABLED_COUNTRIES } from '../international-offers-constants';
import { ClickoutUrl } from '@/helpers/clickout-helper';

export const useInternationalOffersStatus = ({
	loading = ref(false),
	title,
}: {
	loading?: Ref<boolean>;
	title: MaybeRefOrGetter<TitleDetail>;
}) => {
	const { country } = useLanguageStore();
	const { newBuyboxView } = useUserStore();
	const _title = toValue(title);

	const hasDomesticOffers = computed(() => {
		// rewritten to not use monetization typed offers instead of `title.offers`
		return FILTERABLE_BUYBOX_KEYS.some(key => {
			return (_title[key] || []).some(offer => offer.country === country.value);
		});
	});
	const hasInternationalOffers = computed(() => {
		return FILTERABLE_BUYBOX_KEYS.some(key => {
			return (_title[key] || []).some(offer => offer.country !== country.value);
		});
	});
	const isActive = computed(() => {
		return (
			INTERNATIONAL_OFFERS_ENABLED_COUNTRIES.includes(country.value) &&
			newBuyboxView.value === NewBuyBoxView.LIST &&
			!loading.value &&
			!hasDomesticOffers.value &&
			_title.content.upcomingReleases.length === 0
		);
	});

	const internationalOfferTagsByCountry = {
		// CA: 'amz-ca-ft-no-20',
		// AU: 'amz-au-ft-no-22',
		US: 'amz-us-ft-no-20',
		DE: 'amz-de-ft-no-21',
		GB: 'amz-gb-ft-no-21',
		ES: 'amz-es-ft-no-21',
		IT: 'amz-it-ft-no-21',
		FR: 'amz-fr-ft-no-21',
	} as Record<string, string>;

	function setNoOfferTag(url: ClickoutUrl) {
		// only set no offer tag, if
		// - current country is in the internationalOfferTagsByCountry
		// - has NO domestic offers
		// - has NO upcoming releases
		if (Object.keys(internationalOfferTagsByCountry).includes(country.value)) {
			if (!hasDomesticOffers.value && _title.content.upcomingReleases.length === 0) {
				return url
					.setOnOffer('tag', internationalOfferTagsByCountry[country.value])
					.skipUCT2()
					.skipYieldOptimization();
			}
		}
		return url;
	}

	return {
		isActive,
		hasDomesticOffers, // sometimes we care to know if the page has or hasn't any offers, unrelated to international offers
		hasInternationalOffers,
		setNoOfferTag,
	};
};

export const useInternationalOffers = () => {
	const rowCountry = injectLocal<ComputedRef<string | null>>(
		'row-country',
		computed(() => null)
	);

	return {
		rowCountry,
	};
};
